/**
 * Key receipt (Schlüsselquittung) domain API functions (K1b).
 *
 * Tenancy-level receipts + their key items + PDF generate/url. All authenticated
 * via getApiHeaders(); errors surfaced through parseAdminErrorResponse, mirroring
 * the other adminData domain modules (e.g. rentalContracts.js).
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

/** GET /api/admin/tenancies/{tenancyId}/key-receipts -> { items: [...] } */
export async function listKeyReceipts(tenancyId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/key-receipts`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselquittungen konnten nicht geladen werden.");
  }
  return res.json();
}

/** POST /api/admin/tenancies/{tenancyId}/key-receipts -> receipt detail */
export async function createKeyReceipt(tenancyId, body = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/key-receipts`,
    { method: "POST", headers: getApiHeaders(), body: JSON.stringify(body || {}) }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselquittung konnte nicht erstellt werden.");
  }
  return res.json();
}

/** GET /api/admin/key-receipts/{receiptId} -> receipt detail (with items[]) */
export async function getKeyReceipt(receiptId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselquittung konnte nicht geladen werden.");
  }
  return res.json();
}

/** PATCH /api/admin/key-receipts/{receiptId} -> receipt detail */
export async function patchKeyReceipt(receiptId, body = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}`,
    { method: "PATCH", headers: getApiHeaders(), body: JSON.stringify(body || {}) }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Änderungen konnten nicht gespeichert werden.");
  }
  return res.json();
}

/** POST /api/admin/key-receipts/{receiptId}/items -> item */
export async function addKeyReceiptItem(receiptId, body = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/items`,
    { method: "POST", headers: getApiHeaders(), body: JSON.stringify(body || {}) }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselposition konnte nicht hinzugefügt werden.");
  }
  return res.json();
}

/** PATCH /api/admin/key-receipts/{receiptId}/items/{itemId} -> item */
export async function patchKeyReceiptItem(receiptId, itemId, body = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/items/${encodeURIComponent(itemId)}`,
    { method: "PATCH", headers: getApiHeaders(), body: JSON.stringify(body || {}) }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselposition konnte nicht gespeichert werden.");
  }
  return res.json();
}

/** DELETE /api/admin/key-receipts/{receiptId}/items/{itemId} -> { ok } */
export async function deleteKeyReceiptItem(receiptId, itemId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselposition konnte nicht gelöscht werden.");
  }
  return res.json();
}

/** POST /api/admin/key-receipts/{receiptId}/pdf/generate[?force=true] -> metadata */
export async function generateKeyReceiptPdf(receiptId, { force = false } = {}) {
  const qs = force ? "?force=true" : "";
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/pdf/generate${qs}`,
    { method: "POST", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "PDF konnte nicht generiert werden.");
  }
  return res.json();
}

/** GET /api/admin/key-receipts/{receiptId}/pdf/url -> { url } */
export async function getKeyReceiptPdfUrl(receiptId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/pdf/url`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "PDF-Link konnte nicht geladen werden.");
  }
  return res.json();
}

/**
 * POST /api/admin/key-receipts/{receiptId}/signature/sign -> signed metadata
 * (K2c). Sign the receipt with a handwritten tablet signature; backend stores
 * the signature PNG + the signed PDF and locks the receipt (status=signed).
 */
export async function signKeyReceipt(receiptId, { signed_by_name, signature_data_url } = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/signature/sign`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ signed_by_name, signature_data_url }),
    }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselquittung konnte nicht unterzeichnet werden.");
  }
  return res.json();
}

/**
 * POST /api/admin/key-receipts/{receiptId}/signature/sign-dual -> signed metadata
 * (K2d). Dual-sign: tenant + management/landlord representative both sign in the
 * same session; backend stores both signature PNGs + the signed PDF and locks
 * the receipt (status=signed).
 */
export async function signKeyReceiptDual(
  receiptId,
  {
    signed_by_name,
    signature_data_url,
    management_signed_by_name,
    management_signature_data_url,
  } = {}
) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/signature/sign-dual`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({
        signed_by_name,
        signature_data_url,
        management_signed_by_name,
        management_signature_data_url,
      }),
    }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Schlüsselquittung konnte nicht unterzeichnet werden.");
  }
  return res.json();
}

/** GET /api/admin/key-receipts/{receiptId}/signed-pdf/url -> { url } */
export async function getKeyReceiptSignedPdfUrl(receiptId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/key-receipts/${encodeURIComponent(receiptId)}/signed-pdf/url`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Unterschriebenes PDF konnte nicht geladen werden.");
  }
  return res.json();
}
